import { z } from "zod";
import { useFunnel } from "@airhelp/funnel";
import { FunnelStepContainer } from "../../../components/funnel/ui-elements/FunnelStepContainer";
import { FunnelStepSection } from "../../../components/funnel/ui-elements/FunnelStepSection";
import { useTranslation } from "react-i18next";
import { ControlledRadioGroup, useForm, useFormHandlers } from "../../../modules/form";
import {
  FunnelStepBackControl,
  FunnelStepControls,
  FunnelStepForwardControl,
} from "../../../components/funnel/ui-elements/FunnelStepControls";
import {
  getArrivalAirport,
  getDepartureAirport,
  selectDisruptedFlightByIndex,
  updateFlightDetails,
  updateItineraryFlights,
  usePreEligibilityDataSnapshot,
} from "../state/preEligibilityState";
import { ErrorNotification } from "v2/components/funnel/ui-elements/ErrorNotification";
import { trackClaimProcess } from "../../../modules/analytics/track";
import { useTrackEnquiryStepComplete } from "../../../modules/analytics/hooks/useTrackEnquiryStepComplete";
import { isFirstStep } from "../state/appState";

const options = ["no", "yes"] as const;

const stepSchema = z.object({
  hasConnections: z.enum(options),
});

export function AnyConnectionsStep() {
  const funnel = useFunnel();
  const { t } = useTranslation();
  const { flightDetails, itineraryFlights } = usePreEligibilityDataSnapshot();

  const arrivalAirport = getArrivalAirport();
  const departureAirport = getDepartureAirport();

  const trackEnquiryStepComplete = useTrackEnquiryStepComplete({
    currentStep: funnel.currentStep?.name,
  });

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm({
    schema: stepSchema,
    defaultValues: {
      hasConnections:
        flightDetails.anyConnections || itineraryFlights.length > 1
          ? ("yes" as const)
          : ("no" as const),
    },
  });

  const { onSubmit, showError } = useFormHandlers({
    schema: stepSchema,
    onSubmit({ hasConnections }) {
      const isConnections = hasConnections === "yes";
      const noConnections = hasConnections === "no";

      updateFlightDetails({ anyConnections: isConnections });

      /**
       * If the option "no connections" selected and the actual state contains multiple flights,
       * create a new flight to replace the existing journey.
       */
      if (noConnections && itineraryFlights.length > 1) {
        const newFlight = {
          flightDate: null,
          airline: null,
          disrupted: true,
          flightNumber: null,
          departureAirport: departureAirport,
          arrivalAirport: arrivalAirport,
        };

        updateItineraryFlights([newFlight]);
      }

      /**
       * If the option "no connections" selected and the actual state contains only one flight,
       * mark the flight as disrupted.
       */
      if (noConnections && itineraryFlights.length === 1) {
        selectDisruptedFlightByIndex(0);
      }

      trackClaimProcess({ funnelStep: funnel.currentStep?.name });
      trackEnquiryStepComplete();

      funnel.stepForward();
    },
  });

  return (
    <FunnelStepContainer data-testid="anyConnections">
      <FunnelStepSection
        title={t("steps.any_connections.text_mobile", {
          departure: departureAirport?.city,
          departure_iata: departureAirport?.iata,
          arrival: arrivalAirport?.city,
          arrival_iata: arrivalAirport?.iata,
        })}
      >
        <ControlledRadioGroup
          name={"hasConnections"}
          control={control}
          options={options}
          renderOption={(option) => t(`steps.any_connections.answer_${option}`)}
        />
      </FunnelStepSection>

      {showError && <ErrorNotification message={t("components.funnel_step.try_again")} />}

      <FunnelStepControls>
        {!isFirstStep(funnel.currentStep?.uri) && <FunnelStepBackControl />}
        <FunnelStepForwardControl
          isDisabled={!isValid}
          isLoading={isSubmitting}
          onClick={handleSubmit(onSubmit)}
        />
      </FunnelStepControls>
    </FunnelStepContainer>
  );
}
